import { queryOptions } from "@tanstack/react-query";

import { getActiveSchemaVersion, getMasterType, listSchemaVersions } from "@/features/schema/api";
import { ApiError } from "@/lib/api/problem";

export function masterTypeQuery(code: string, locale: string) {
  return queryOptions({
    queryKey: ["master-type", code, { locale }],
    queryFn: () => getMasterType(code, locale),
  });
}

/**
 * The schema a form is built from.
 *
 * `staleTime` is generous on purpose. A schema version changes when a
 * reviewer approves one, which is rare and deliberate — refetching it on
 * every window focus would be a request per field the user tabs away
 * from. DEE-28's fourth criterion is that a version change shows up *on
 * next load*, not instantly, and this is what makes that the honest
 * promise rather than an accident of caching.
 */
export function activeSchemaQuery(code: string) {
  return queryOptions({
    queryKey: ["schema", code, "active"],
    // Resolves to `null` rather than throwing when the type has no active
    // version. That is a real state — a master type exists before its
    // first schema is approved, and records written under a since-retired
    // one are still readable — so it belongs in the type rather than in an
    // error branch every caller has to remember to write.
    queryFn: async () => {
      try {
        return await getActiveSchemaVersion(code);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function schemaVersionsQuery(code: string) {
  return queryOptions({
    queryKey: ["schema", code, "versions"],
    queryFn: () => listSchemaVersions(code, { limit: 100 }),
  });
}
